import { useEffect, useRef, useState } from "react";

/** ---------------------- Tipos da API ---------------------- */
export type PerdaItemAPI = {
  id: number;
  movimentacao_id: number;
  produto_id: number;
  quantidade: number;
  custo_unitario: string | null; // NUMERIC (string)
  produto: {
    id: number;
    codigo: string;
    nome: string;
    posicao: string | null;
  };
};

export type PerdaMovAPI = {
  movimentacao: {
    id: number;
    responsavel_id: number | null;
    responsavel_nome: string | null;
    departamento_origem: string | null;
    criado_em: string;
    motivo: string | null;
    tipo?: string | null; // 👈 opcional
    tipo_full?: string | null; // 👈 opcional
  };
  itens: PerdaItemAPI[];
};

type RespAPI = {
  ok: boolean;
  meta: {
    filtros: {
      date_from: string | null;
      date_to: string | null;
      order: "ASC" | "DESC";
      limit: number;
      offset: number;
    };
    count: number;
  };
  data: PerdaMovAPI[];
};

/** ---------------------- Constantes ---------------------- */
const BASE = "https://api.eva-googlemarine.com/departamentos/recursos-humanos";
const URL = `${BASE}/perdas`;
const URL_AWAIT = `https://api.eva-googlemarine.com/departamentos/suprimentos/perdas/await`;

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;
const AWAIT_TIMEOUT_MS = 3000;
const AWAIT_GAP_MS = 120;

/** ---------------------- Utils ---------------------- */
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const jitter = (ms: number) => ms + Math.floor(Math.random() * 400);

function toBRT(iso: string): { data: string; hora: string } {
  if (!iso) return { data: "-", hora: "-" };

  // Match direto na string ISO: "2025-09-26T04:57:46.577Z"
  const m = iso.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?Z$/
  );
  if (!m) {
    return { data: iso, hora: "" }; // fallback: mostra string crua
  }

  const [, ano, mes, dia, hora, minuto] = m;
  return {
    data: `${dia}/${mes}/${ano}`,
    hora: `${hora}:${minuto}`,
  };
}

function formatBRL(x: number): string {
  return x.toLocaleString("pt-BR", { minimumFractionDigits: 2 });
}

/** Linha preparada para a Tabela (ordem = seus headers) */
/** Ordem = headers:
 * ["Responsável","Código","Tipo","Nome do Produto","Custo Unitário","Quantidade","Motivo","Data","Hora"]
 */
export type PerdaRowUI = [
  string, // Responsável
  string, // Código
  string, // Tipo         👈 NOVO
  string, // Nome do Produto
  string, // Custo Unitário
  string, // Quantidade
  string, // Motivo
  string, // Data
  string // Hora
];

/** Normaliza a resposta da API para linhas da tabela */
function flattenToRows(movs: PerdaMovAPI[]): PerdaRowUI[] {
  const rows: PerdaRowUI[] = [];

  for (const m of movs) {
    const { data, hora } = toBRT(m.movimentacao.criado_em);
    const responsavel = m.movimentacao.responsavel_nome?.trim() || "-";
    const motivo = m.movimentacao.motivo?.trim() || "-";
    const tipo =
      (m.movimentacao.tipo && m.movimentacao.tipo.trim()) ||
      (m.movimentacao.tipo_full?.trim() ? "perda" : "perda"); // default “perda”

    for (const it of m.itens) {
      const qtd = Number(it.quantidade || 0);
      const custoNum = Number(it.custo_unitario ?? "0");
      const custoFmt = `R$ ${formatBRL(custoNum)}`;

      rows.push([
        responsavel, // Responsável
        it.produto.codigo, // Código
        tipo, // Tipo   👈 NOVO
        it.produto.nome, // Nome do Produto
        custoFmt, // Custo Unitário
        String(qtd), // Quantidade
        motivo, // Motivo
        data, // Data
        hora, // Hora
      ]);
    }
  }

  return rows;
}

/** ---------------------- Fetchers ---------------------- */
export type PerdasQuery = Partial<{
  date_from: string; // YYYY-MM-DD
  date_to: string; // YYYY-MM-DD
  order: "asc" | "desc";
  limit: number;
  offset: number;
}>;

function buildQuery(q?: PerdasQuery): string {
  if (!q) return "";
  const p = new URLSearchParams();
  if (q.date_from) p.set("date_from", q.date_from);
  if (q.date_to) p.set("date_to", q.date_to);
  if (q.order) p.set("order", q.order);
  if (typeof q.limit === "number") p.set("limit", String(q.limit));
  if (typeof q.offset === "number") p.set("offset", String(q.offset));
  const s = p.toString();
  return s ? `?${s}` : "";
}

export async function fetchPerdasOnce(
  query?: PerdasQuery
): Promise<{ rows: PerdaRowUI[]; meta: RespAPI["meta"] | null }> {
  const res = await fetch(`${URL}${buildQuery(query)}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = (await res.json()) as RespAPI;
  if (!json.ok) return { rows: [], meta: json.meta ?? null };
  return { rows: flattenToRows(json.data), meta: json.meta ?? null };
}

/** ---------------------- Await (long-poll) ---------------------- */
type AwaitResult = { changed: boolean; versao: number | null };

async function awaitChange(
  since: number,
  signal?: AbortSignal
): Promise<AwaitResult | null> {
  try {
    const url = `${URL_AWAIT}?since=${encodeURIComponent(
      since
    )}&timeout_ms=${AWAIT_TIMEOUT_MS}`;
    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    });
    if (!res.ok) return null;
    const json = await res.json();
    const versao =
      typeof json?.versao === "number" ? (json.versao as number) : since;
    const changed = Boolean(json?.changed);
    return { changed, versao };
  } catch {
    return null;
  }
}

/** ---------------------- Hook ---------------------- */
export function usePerdasSuprimentos(query?: PerdasQuery) {
  const [rows, setRows] = useState<PerdaRowUI[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<RespAPI["meta"] | null>(null);

  const stop = useRef(false);
  const fetching = useRef(false);

  const versaoRef = useRef<number | null>(null);
  const awaitCtrlRef = useRef<AbortController | null>(null);
  const awaitSupported = useRef<boolean | null>(null);
  const awaitInFlightRef = useRef(false);

  async function carregar() {
    fetching.current = true;
    try {
      const r = await fetchPerdasOnce(query);
      setRows(r.rows);
      setMeta(r.meta);
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;
    versaoRef.current = null;
    awaitSupported.current = null;
    awaitCtrlRef.current?.abort();

    (async () => {
      try {
        await carregar();
        setLoading(false);

        const init = await awaitChange(-1);
        if (init && typeof init.versao === "number") {
          versaoRef.current = init.versao;
          awaitSupported.current = true;
        } else {
          awaitSupported.current = false;
        }

        while (!stop.current) {
          while (fetching.current && !stop.current) await sleep(25);
          if (stop.current) break;

          const cur = versaoRef.current;

          if (awaitSupported.current !== false && cur != null) {
            if (awaitInFlightRef.current) {
              await sleep(50);
              continue;
            }

            const ac = new AbortController();
            awaitCtrlRef.current = ac;
            awaitInFlightRef.current = true;

            let resp: AwaitResult | null = null;
            try {
              resp = await awaitChange(cur, ac.signal);
            } catch (e: any) {
              if (e?.name !== "AbortError")
                await sleep(jitter(ERROR_BACKOFF_MS));
            } finally {
              if (awaitCtrlRef.current === ac) awaitCtrlRef.current = null;
              awaitInFlightRef.current = false;
            }

            if (stop.current) break;

            if (resp === null) {
              if (awaitSupported.current === null)
                awaitSupported.current = false;
              await sleep(POLL_FALLBACK_MS);
              continue;
            } else if (awaitSupported.current === null) {
              awaitSupported.current = true;
            }

            const avancou = (resp.versao ?? -1) > (cur ?? -1);
            if (resp.changed || avancou) {
              versaoRef.current = resp.versao ?? cur;
              await carregar();
              await sleep(20);
            } else {
              await sleep(AWAIT_GAP_MS);
            }
            continue;
          }

          await sleep(POLL_FALLBACK_MS);
          await carregar();
        }
      } catch (e) {
        if (!stop.current) console.error("usePerdasSuprimentos loop error:", e);
        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      awaitCtrlRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(query)]);

  const refresh = async () => {
    while (fetching.current) await sleep(25);
    await carregar();
  };

  return { rows, loading, refresh, meta };
}
